import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useSelector } from "react-redux";
import { CashkaroService } from "../../../core/services";
import { CashkaroConversion, CashkaroEarningsSummary, CashkaroEarningsView, CashkaroPerformanceRow } from "./types";
import { CC } from "./CashkaroLayout";

const STATUS_HEX: Record<string, string> = {
  approved: "#2e7d32",
  pending: "#ed6c02",
  cancelled: "#d32f2f",
  rejected: "#9e9e9e",
};

// Same chart (clicks by whichever dimension is already loaded for the
// current view) drives both the table below it and this visual — no extra
// Trackier calls. "Overall" has nothing to break a single aggregate row
// into, so it shows a conversion-status donut instead, built from the
// conversion log that's already fetched regardless of view.
function buildChartOptions(view: CashkaroEarningsView, performance: CashkaroPerformanceRow[], conversions: CashkaroConversion[]): Highcharts.Options | null {
  if (view === "overall") {
    const counts: Record<string, number> = {};
    for (const c of conversions) counts[c.status] = (counts[c.status] || 0) + 1;
    const data = Object.entries(counts)
      .filter(([, y]) => y > 0)
      .map(([name, y]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), y, color: STATUS_HEX[name] || "#999" }));
    if (data.length === 0) return null;
    return {
      chart: { type: "pie", height: 300, backgroundColor: "transparent" },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: { pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)" },
      legend: { enabled: true, itemStyle: { fontWeight: "600", fontFamily: CC.font } },
      plotOptions: { pie: { innerSize: "65%", borderWidth: 2, borderColor: "#fff", dataLabels: { enabled: false } } },
      series: [{ type: "pie", name: "Conversions", data }],
    };
  }

  if (performance.length === 0) return null;

  if (view === "daily") {
    const sorted = [...performance].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    return {
      chart: { type: "areaspline", height: 300 },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        categories: sorted.map((r) => (r.date ? new Date(r.date).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "")),
        lineColor: CC.border,
        labels: { style: { fontFamily: CC.font, fontSize: "12px" } },
      },
      yAxis: { title: { text: "Clicks" }, allowDecimals: false, gridLineColor: "#f0f0f0", min: 0, labels: { style: { fontFamily: CC.font } } },
      legend: { enabled: false },
      tooltip: { pointFormat: "<b>{point.y}</b> clicks" },
      plotOptions: {
        areaspline: {
          color: CC.primary,
          lineWidth: 3,
          marker: { fillColor: CC.primary, radius: 4 },
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${CC.primary}66`],
              [1, `${CC.primary}00`],
            ],
          },
        },
      },
      series: [{ type: "areaspline", name: "Clicks", data: sorted.map((r) => r.clicks) }],
    };
  }

  // campaign / geo: bar chart of clicks by dimension, top 8 by clicks.
  const labeled = performance
    .map((r) => ({ label: view === "campaign" ? r.campaign_name || `Campaign #${r.campaign_id}` : r.country || "Unknown", clicks: r.clicks }))
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, 8);
  return {
    chart: { type: "column", height: 300 },
    title: { text: undefined },
    credits: { enabled: false },
    xAxis: { categories: labeled.map((r) => r.label), lineColor: CC.border, labels: { style: { fontFamily: CC.font, fontSize: "12px" } } },
    yAxis: { title: { text: "Clicks" }, allowDecimals: false, gridLineColor: "#f0f0f0", min: 0, labels: { style: { fontFamily: CC.font } } },
    legend: { enabled: false },
    tooltip: { pointFormat: "<b>{point.y}</b> clicks" },
    plotOptions: { column: { color: CC.primary, borderRadius: 4, pointPadding: 0.15, borderWidth: 0 } },
    series: [{ type: "column", name: "Clicks", data: labeled.map((r) => r.clicks) }],
  };
}

const STATUS_COLOR: Record<string, "success" | "warning" | "default" | "error"> = {
  approved: "success",
  pending: "warning",
  cancelled: "error",
  rejected: "error",
};

const VIEW_OPTIONS: { value: CashkaroEarningsView; label: string }[] = [
  { value: "overall", label: "Overall" },
  { value: "daily", label: "Daily" },
  { value: "campaign", label: "By Campaign" },
  { value: "geo", label: "By Geography" },
];

// Which column to render as the row's dimension label, per view — the rest
// of each row (clicks/conversions/sale amount/payout) is the same regardless
// of view since the backend always groups by `source` first and this second
// dimension second (see Cashkaro.getEarnings on the backend).
const DIMENSION_COLUMN: Record<CashkaroEarningsView, { header: string; render: (row: CashkaroPerformanceRow) => React.ReactNode } | null> = {
  overall: null,
  daily: { header: "Date", render: (row) => (row.date ? new Date(row.date).toLocaleDateString() : "—") },
  campaign: { header: "Campaign", render: (row) => row.campaign_name || `Campaign #${row.campaign_id}` },
  geo: { header: "Country", render: (row) => row.country || "Unknown" },
};

// Stat tile matching the Zap Storefront analytics page's card: label+value on
// the left, a soft-tinted icon tile on the right, flat border with a hover
// lift — one shared visual language for every "KPI strip" across the app.
const StatTile: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: CC.radiusLg,
      bgcolor: CC.card,
      border: `1px solid ${CC.border}`,
      transition: ".2s",
      "&:hover": { transform: "translateY(-4px)", boxShadow: "0 20px 40px rgba(15,23,42,.08)" },
    }}
  >
    <Box display="flex" justifyContent="space-between">
      <Box minWidth={0}>
        <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font, fontSize: 13 }}>{label}</Typography>
        <Typography sx={{ fontFamily: CC.font, fontSize: 28, fontWeight: 800, mt: 1, color: CC.textPrimary }} noWrap>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: CC.radiusMd,
          bgcolor: `${color}15`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    </Box>
  </Paper>
);

// A whole panel — title bar (with its own bottom border) wrapping whatever
// content is passed in — so "Performance" and "Conversion log" each read as
// one bordered card instead of a heading floating above a separately-bordered
// table.
const SectionPanel: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <Paper elevation={0} sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden", mb: 3 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: { xs: 2, md: 3 },
        py: 1.75,
        borderBottom: `1px solid ${CC.border}`,
        bgcolor: CC.bg,
      }}
    >
      <Box sx={{ color: CC.primary, display: "flex" }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const toStr = (d: Date) => d.toISOString().slice(0, 10);
  return { start: toStr(start), end: toStr(end) };
}

export const CashkaroEarnings: React.FC = () => {
  const user = useSelector((state: any) => state.UserReducer?.UserData);
  const influencerId = user?.influencer_id;

  const [range, setRange] = useState(defaultRange());
  const [view, setView] = useState<CashkaroEarningsView>("campaign");
  const [summary, setSummary] = useState<CashkaroEarningsSummary | null>(null);
  const [conversions, setConversions] = useState<CashkaroConversion[]>([]);
  const [performance, setPerformance] = useState<CashkaroPerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!influencerId) return;
    setLoading(true);
    CashkaroService.getEarnings({ influencer_id: influencerId, start: range.start, end: range.end, group_by: view })
      .then((res) => {
        setSummary(res?.data?.summary || null);
        const list = res?.data?.conversions;
        setConversions(Array.isArray(list) ? list : []);
        const perf = res?.data?.performance;
        setPerformance(Array.isArray(perf) ? perf : []);
      })
      .catch(() => {
        setSummary(null);
        setConversions([]);
        setPerformance([]);
      })
      .finally(() => setLoading(false));
  }, [influencerId, range.start, range.end, view]);

  const dimension = DIMENSION_COLUMN[view];
  const chartOptions = useMemo(() => buildChartOptions(view, performance, conversions), [view, performance, conversions]);

  const fieldSx = {
    bgcolor: CC.card,
    "& .MuiOutlinedInput-root": {
      borderRadius: CC.radiusSm,
      fontFamily: CC.font,
      "& fieldset": { borderColor: CC.border },
      "&:hover fieldset": { borderColor: CC.borderHover },
      "&.Mui-focused fieldset": { borderColor: CC.primary },
    },
    "& .MuiInputLabel-root": { fontFamily: CC.font },
  };

  const tableHeadSx = {
    "& th": {
      bgcolor: CC.bg,
      fontWeight: 700,
      fontFamily: CC.font,
      border: 0,
      borderBottom: `1px solid ${CC.border}`,
      color: CC.textPrimary,
    },
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "wrap" },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font, lineHeight: 1.2 }}>
            Earnings overview
          </Typography>
          <Typography variant="body2" sx={{ color: CC.textSecondary, fontFamily: CC.font, mt: 0.25 }}>
            Track clicks, conversions and payouts from your brand deal links.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, marginTop: {xs:1.5, lg:0 }, flexWrap: "wrap", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={range.start}
            onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: 160 }, ...fieldSx }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={range.end}
            onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: 160 }, ...fieldSx }}
          />
          <TextField
            label="View by"
            select
            size="small"
            value={view}
            onChange={(e) => setView(e.target.value as CashkaroEarningsView)}
            sx={{ width: { xs: "100%", sm: 160 }, ...fieldSx }}
          >
            {VIEW_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: CC.font }}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={26} sx={{ color: CC.primary }} />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} md={3}>
              <StatTile label="Clicks" value={String(summary?.clicks ?? 0)} icon={<TouchAppOutlinedIcon fontSize="small" />} color={CC.primary} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Approved Payout"
                value={`₹${Number(summary?.approved_payout || 0).toFixed(2)}`}
                icon={<CheckCircleOutlineOutlinedIcon fontSize="small" />}
                color="#16A34A"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Pending Payout"
                value={`₹${Number(summary?.pending_payout || 0).toFixed(2)}`}
                icon={<ScheduleOutlinedIcon fontSize="small" />}
                color="#D97706"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Total Conversions"
                value={String(summary?.total_conversions ?? 0)}
                icon={<ShoppingCartOutlinedIcon fontSize="small" />}
                color="#7C3AED"
              />
            </Grid>
          </Grid>

          <SectionPanel
            icon={<BarChartOutlinedIcon fontSize="small" />}
            title={view === "overall" ? "Conversion status breakdown" : `Performance — ${VIEW_OPTIONS.find((o) => o.value === view)?.label}`}
          >
            {view === "overall" && !chartOptions ? (
              <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font, py: 4, textAlign: "center" }}>
                No conversions in this date range yet.
              </Typography>
            ) : (
              chartOptions && (
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                </Box>
              )
            )}

            {dimension && (
              <>
                {performance.length === 0 ? (
                  <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font, py: 4, textAlign: "center" }}>
                    No clicks on your links in this date range yet.
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: "auto", borderTop: chartOptions ? `1px solid ${CC.border}` : "none" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={tableHeadSx}>
                          <TableCell>{dimension.header}</TableCell>
                          <TableCell align="right">Clicks</TableCell>
                          <TableCell align="right">Approved Conversions</TableCell>
                          <TableCell align="right">Sale Amount</TableCell>
                          <TableCell align="right">Payout</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ "& td": { fontFamily: CC.font, borderColor: CC.border } }}>
                        {performance.map((row, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontWeight: 600, color: CC.textPrimary }}>{dimension.render(row)}</TableCell>
                            <TableCell align="right">{row.clicks}</TableCell>
                            <TableCell align="right">{row.approved_conversions}</TableCell>
                            <TableCell align="right">{Number(row.sale_amount || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{Number(row.payout || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </>
            )}
          </SectionPanel>

          <SectionPanel icon={<ReceiptLongOutlinedIcon fontSize="small" />} title="Conversion log">
            {conversions.length === 0 ? (
              <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font, py: 4, textAlign: "center" }}>
                No conversions in this date range yet.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={tableHeadSx}>
                      <TableCell>Campaign</TableCell>
                      <TableCell>Goal</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Payout</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ "& td": { fontFamily: CC.font, borderColor: CC.border } }}>
                    {conversions.map((conv) => (
                      <TableRow key={conv.trackier_conversion_id} hover>
                        <TableCell sx={{ fontWeight: 600, color: CC.textPrimary }}>{conv.campaign_name || `Campaign #${conv.campaign_id}`}</TableCell>
                        <TableCell sx={{ color: CC.textSecondary }}>{conv.goal_name || "—"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={conv.status} color={STATUS_COLOR[conv.status] || "default"} sx={{ height: 22, fontSize: 11, borderRadius: "999px" }} />
                        </TableCell>
                        <TableCell align="right">
                          {conv.currency || ""} {Number(conv.payout || 0).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ color: CC.textSecondary }}>
                          {conv.trackier_created_at ? new Date(conv.trackier_created_at).toLocaleDateString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </SectionPanel>
        </>
      )}
    </Box>
  );
};

export default CashkaroEarnings;
